/**
 * MemberAdminSection — Innstillinger → Administrasjon → Familiemedlemmer.
 *
 * The admin surface for managing the family roster after onboarding:
 *   - lists every member (archived shown muted, last)
 *   - "Legg til" opens <MemberForm> in create mode
 *   - each active row → Inviter / Nytt passord / Rediger / Arkiver;
 *     archived rows → Gjenopprett
 *   - hver rad viser brukernavnet og om medlemmet har passord og PIN (#141)
 *   - og allergier og kosthold (DietaryPills, #159): de er sikkerhetskritiske
 *     og skal ikke bare stå inne i redigeringsskjemaet
 *
 * #159 (redesign steg 9a): åpnes fra raden «N medlemmer · Inviter / legg til»
 * på Familie. Listen og skjemaet er ikke tegnet i skissene ennå, så de har
 * bare fått de nye tokenene: InitialAvatar (D2) i stedet for fargeprikken, og
 * flater, kanter og tekst fra rolletokenene.
 *
 * Admin-scope recovery: every mutation runs through `runGuarded`. A 403
 * admin-scope error opens <AdminReauthModal>; on successful re-auth the
 * original action is retried automatically, so the admin never loses their
 * place in the form.
 */
import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AdminCreateMemberInput,
  AdminUpdateMemberInput,
  ColorSlot,
  MemberAdminRow,
} from '@stell/shared';
import { Button, LoadingState, ErrorState, Pill } from '../../components/primitives';
import { InitialAvatar, asColorSlot } from '../../components/primitives/InitialAvatar';
import { DietaryPills } from './DietaryPills';
import { MemberForm, type MemberFormPayload } from './MemberForm';
import { asApiError, authErrorMessage } from '../auth/authApi';
import { AdminReauthModal } from './AdminReauthModal';
import { AdminScopeLocked } from './AdminScopeLocked';
import {
  type ApiError,
  isAdminScopeError,
  useAdminMembers,
  useArchiveMember,
  useCreateMemberInvite,
  useCreateMember,
  useCreatePasswordReset,
  useUnarchiveMember,
  useUpdateMember,
} from './useMembers';

type View = { kind: 'list' } | { kind: 'create' } | { kind: 'edit'; row: MemberAdminRow };

/** Invitasjons- eller tilbakestillingslenken som vises i dialogen. */
interface LinkResult {
  kind: 'invite' | 'reset';
  memberName: string;
  url: string;
  expiresAt: string;
}

function toCreateInput(p: MemberFormPayload): AdminCreateMemberInput {
  // Brukernavn, passord og PIN er valgfrie (#141) — utelat dem som ikke er satt.
  const { pin, pinConfirm, username, password, passwordConfirm, ...base } = p;
  return {
    ...base,
    ...(username !== undefined ? { username } : {}),
    ...(password !== undefined ? { password, passwordConfirm } : {}),
    ...(pin !== undefined ? { pin, pinConfirm } : {}),
  };
}

function toUpdateInput(p: MemberFormPayload): AdminUpdateMemberInput {
  const base: AdminUpdateMemberInput = {
    name: p.name,
    shortName: p.shortName,
    role: p.role,
    birthDate: p.birthDate,
    colorSlot: p.colorSlot,
    allergies: p.allergies,
    dietary: p.dietary,
    notes: p.notes,
    ...(p.username !== undefined ? { username: p.username } : {}),
  };
  if (p.pin !== undefined) {
    return { ...base, pin: p.pin, pinConfirm: p.pinConfirm };
  }
  return base;
}

export function MemberAdminSection(): ReactElement {
  const { t } = useTranslation();
  const list = useAdminMembers();
  const createMut = useCreateMember();
  const inviteMut = useCreateMemberInvite();
  const resetMut = useCreatePasswordReset();
  const updateMut = useUpdateMember();
  const archiveMut = useArchiveMember();
  const unarchiveMut = useUnarchiveMember();

  const [view, setView] = useState<View>({ kind: 'list' });
  const [formError, setFormError] = useState<ApiError | null>(null);
  const [actionError, setActionError] = useState<ApiError | null>(null);
  const [confirmArchive, setConfirmArchive] = useState<MemberAdminRow | null>(null);
  const [linkResult, setLinkResult] = useState<LinkResult | null>(null);
  const [reauth, setReauth] = useState<{ open: boolean; retry: (() => void) | null }>({
    open: false,
    retry: null,
  });
  const editingRow = view.kind === 'edit' ? view.row : undefined;
  const takenSlots = useMemo(() => {
    return (list.data ?? []).reduce<ColorSlot[]>((acc, m) => {
      if (!m.archived_at && m.id !== editingRow?.id) {
        acc.push(m.color_token as ColorSlot);
      }
      return acc;
    }, []);
  }, [list.data, editingRow?.id]);

  /** Run a mutation; on a 403 admin-scope error open re-auth and stash retry. */
  async function runGuarded(fn: () => Promise<void>, onError: (err: ApiError) => void): Promise<void> {
    try {
      await fn();
    } catch (err) {
      if (isAdminScopeError(err)) {
        setReauth({ open: true, retry: () => void runGuarded(fn, onError) });
        return;
      }
      onError(asApiError(err));
    }
  }


  function submitCreate(payload: MemberFormPayload): void {
    setFormError(null);
    void runGuarded(
      async () => {
        await createMut.mutateAsync(toCreateInput(payload));
        setView({ kind: 'list' });
      },
      setFormError,
    );
  }

  function submitEdit(id: string, payload: MemberFormPayload): void {
    setFormError(null);
    void runGuarded(
      async () => {
        await updateMut.mutateAsync({ id, input: toUpdateInput(payload) });
        setView({ kind: 'list' });
      },
      setFormError,
    );
  }

  function doArchive(row: MemberAdminRow): void {
    setActionError(null);
    setConfirmArchive(null);
    void runGuarded(async () => {
      await archiveMut.mutateAsync(row.id);
    }, setActionError);
  }

  function doUnarchive(row: MemberAdminRow): void {
    setActionError(null);
    void runGuarded(async () => {
      await unarchiveMut.mutateAsync(row.id);
    }, setActionError);
  }

  function createInvite(row: MemberAdminRow): void {
    setActionError(null);
    void runGuarded(async () => {
      const result = await inviteMut.mutateAsync(row.id);
      setLinkResult({
        kind: 'invite',
        memberName: result.memberName,
        url: result.inviteUrl,
        expiresAt: result.expiresAt,
      });
    }, setActionError);
  }

  function createPasswordReset(row: MemberAdminRow): void {
    setActionError(null);
    void runGuarded(async () => {
      const result = await resetMut.mutateAsync(row.id);
      setLinkResult({
        kind: 'reset',
        memberName: result.memberName,
        url: result.resetUrl,
        expiresAt: result.expiresAt,
      });
    }, setActionError);
  }

  function onReauthSuccess(): void {
    const retry = reauth.retry;
    setReauth({ open: false, retry: null });
    retry?.();
  }

  // ── Form views ────────────────────────────────────────────────────────────
  if (view.kind === 'create' || view.kind === 'edit') {
    return (
      <div className="space-y-4" data-testid="member-admin-form-view">
        <h3 className="text-heading-s font-extrabold text-ink">
          {view.kind === 'create' ? t('admin.members.add_title') : t('admin.members.edit_title')}
        </h3>
        <MemberForm
          mode={view.kind === 'create' ? 'create' : 'edit'}
          initial={editingRow}
          takenSlots={takenSlots}
          submitting={createMut.isPending || updateMut.isPending}
          error={formError}
          onSubmit={(payload) =>
            view.kind === 'create' ? submitCreate(payload) : submitEdit(view.row.id, payload)
          }
          onCancel={() => {
            setFormError(null);
            setView({ kind: 'list' });
          }}
        />
        <AdminReauthModal
          open={reauth.open}
          onClose={() => setReauth({ open: false, retry: null })}
          onSuccess={onReauthSuccess}
        />
      </div>
    );
  }

  // ── List view ───────────────────────────────────────────────────────────
  return (
    <div className="space-y-4" data-testid="member-admin-section">
      <div className="flex items-center justify-between">
        <h3 className="text-heading-s font-extrabold text-ink">
          {t('admin.members.section_label')}
        </h3>
        <Button onClick={() => setView({ kind: 'create' })} data-testid="member-add">
          {t('admin.members.add')}
        </Button>
      </div>

      {actionError ? (
        <p className="text-sm text-bad" data-testid="member-action-error">
          {authErrorMessage(t, actionError)}
        </p>
      ) : null}

      {list.isLoading ? (
        <LoadingState lines={3} />
      ) : list.isError && isAdminScopeError(list.error) ? (
        // Fresh admin re-auth needed: offer to unlock instead of a dead "Prøv
        // igjen" (the GET stays 403 until scope is granted).
        <AdminScopeLocked testIdPrefix="member" message={t('admin.members.scope_locked')} />
      ) : list.isError ? (
        <ErrorState
          variant="inline"
          heading={t('admin.members.load_error')}
          onRetry={() => void list.refetch()}
        />
      ) : (
        <ul className="divide-y divide-card-line" data-testid="member-list">
          {(list.data ?? []).map((m) => {
            const archived = Boolean(m.archived_at);
            return (
              <li
                key={m.id}
                data-testid={`member-row-${m.id}`}
                // På telefon brytes knappene til en egen linje under navnet, så
                // allergipillene får hele bredden og ikke klemmes (#159).
                className={`flex flex-wrap items-center gap-3 py-3 ${archived ? 'opacity-50' : ''}`}
              >
                <InitialAvatar name={m.name} colorSlot={asColorSlot(m.color_token)} decorative />
                <div className="min-w-0 flex-1 basis-40">
                  <p className="truncate font-bold text-ink">{m.name}</p>
                  <p className="text-xs text-ink-2">
                    {t(`onboarding.step3.role_${m.role}`)}
                    {' '}·{' '}
                    <span
                      data-testid={`member-username-${m.id}`}
                      className={m.username ? 'font-mono' : ''}
                    >
                      {m.username ?? t('admin.members.username_missing')}
                    </span>
                    {archived ? ` · ${t('admin.members.archived')}` : ''}
                  </p>
                  {archived ? null : (
                    <DietaryPills
                      dietary={m.dietary}
                      className="mt-1.5"
                      data-testid={`member-dietary-${m.id}`}
                    />
                  )}
                  {archived ? null : (
                    <p className="mt-1 flex flex-wrap gap-1" data-testid={`member-credentials-${m.id}`}>
                      <Pill variant={m.has_password ? 'ok' : 'warn'}>
                        {t(m.has_password ? 'admin.members.has_password' : 'admin.members.no_password')}
                      </Pill>
                      <Pill variant={m.has_pin ? 'ok' : 'neutral'}>
                        {t(m.has_pin ? 'admin.members.has_pin' : 'admin.members.no_pin')}
                      </Pill>
                    </p>
                  )}
                </div>
                {archived ? (
                  <Button
                    variant="secondary"
                    onClick={() => doUnarchive(m)}
                    data-testid={`member-unarchive-${m.id}`}
                  >
                    {t('admin.members.unarchive')}
                  </Button>
                ) : (
                  <div className="flex basis-full flex-wrap gap-2 sm:basis-auto sm:justify-end">
                    <Button
                      variant="secondary"
                      onClick={() => createInvite(m)}
                      data-testid={`member-invite-${m.id}`}
                    >
                      {t('admin.members.invite')}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => createPasswordReset(m)}
                      data-testid={`member-reset-${m.id}`}
                    >
                      {t('admin.members.reset_password')}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setFormError(null);
                        setView({ kind: 'edit', row: m });
                      }}
                      data-testid={`member-edit-${m.id}`}
                    >
                      {t('admin.members.edit')}
                    </Button>
                    <Button
                      variant="danger"
                      onClick={() => setConfirmArchive(m)}
                      data-testid={`member-archive-${m.id}`}
                    >
                      {t('admin.members.archive')}
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {confirmArchive ? (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-dim p-4"
          data-testid="member-archive-confirm"
        >
          <div className="w-full max-w-sm space-y-4 rounded-lg bg-sheet p-6 shadow-lg">
            <h2 className="text-heading-m font-extrabold text-ink">
              {t('admin.members.archive_confirm_title')}
            </h2>
            <p className="text-sm text-ink-2">
              {t('admin.members.archive_confirm_body', { name: confirmArchive.name })}
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => setConfirmArchive(null)}
                data-testid="member-archive-cancel"
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="danger"
                onClick={() => doArchive(confirmArchive)}
                data-testid="member-archive-confirm-btn"
              >
                {t('admin.members.archive')}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {linkResult ? (
        <LinkDialog result={linkResult} onClose={() => setLinkResult(null)} />
      ) : null}

      <AdminReauthModal
        open={reauth.open}
        onClose={() => setReauth({ open: false, retry: null })}
        onSuccess={onReauthSuccess}
      />
    </div>
  );
}

/**
 * Viser en invitasjons- eller tilbakestillingslenke med kopier-knapp. Admin
 * sender lenken selv (ingen e-post, #139).
 */
function LinkDialog({ result, onClose }: { result: LinkResult; onClose: () => void }): ReactElement {
  const { t } = useTranslation();
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const prefix = result.kind === 'invite' ? 'member-invite' : 'member-reset';
  const title = t(result.kind === 'invite' ? 'admin.members.invite_title' : 'admin.members.reset_title');
  // Kontrakten tillater en relativ sti (`/nytt-passord/:token`); vis alltid full URL.
  const url = new URL(result.url, window.location.origin).toString();

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(url);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${prefix}-title`}
      className="fixed inset-0 z-50 flex items-center justify-center bg-dim p-4"
      data-testid={`${prefix}-dialog`}
    >
      <div className="w-full max-w-lg space-y-4 rounded-lg bg-sheet p-6 shadow-lg">
        <h2 id={`${prefix}-title`} className="text-heading-m font-extrabold text-ink">
          {title}
        </h2>
        <p className="text-sm text-ink-2">
          {t(result.kind === 'invite' ? 'admin.members.invite_body' : 'admin.members.reset_body', {
            name: result.memberName,
          })}
        </p>
        <input
          readOnly
          value={url}
          aria-labelledby={`${prefix}-title`}
          onFocus={(e) => e.currentTarget.select()}
          data-testid={`${prefix}-url`}
          className="field text-sm"
        />
        <p className="text-xs text-ink-2">
          {t('admin.members.invite_expires', {
            at: new Date(result.expiresAt).toLocaleString('nb-NO'),
          })}
        </p>
        {copyState === 'failed' ? (
          <p className="text-sm text-bad" role="alert" data-testid={`${prefix}-copy-error`}>
            {t('admin.members.error.invite_copy')}
          </p>
        ) : null}
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={() => void copy()} data-testid={`${prefix}-copy`}>
            <span aria-live="polite">
              {copyState === 'copied' ? t('admin.members.copied') : t('admin.members.invite_copy')}
            </span>
          </Button>
          <Button variant="ghost" onClick={onClose} data-testid={`${prefix}-close`}>
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    </div>
  );
}
